import { useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import dayjs from "dayjs";
import { Loader2, Plus, Trash2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { SearchableSelect } from "~/components/ui/searchable-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Textarea } from "~/components/ui/textarea";
import {
  useCreateMaquilaConsumption,
  useMaquilaRecipes,
} from "~/hooks/maquila";
import { useOilTypes } from "~/hooks/purchasing/useOilTypes";
import { useRawMaterials } from "~/hooks/purchasing/useRawMaterials";
import type {
  MaquilaIngredientType,
  MaquilaProductRecipe,
} from "~/types/maquila";
import { formatCurrency } from "~/utils/helpers";
import { costPerKgClp } from "~/utils/oilConversions";

import { MaquilaClientSelect } from "./components/MaquilaClientSelect";

interface ConsumeLine {
  key: string;
  ingredient_type: MaquilaIngredientType;
  oil_type_id: string | null;
  raw_material_id: string | null;
  name: string;
  code: string | null;
  unit: string;
  quantity_per_unit: number;
  waste_factor: number;
  quantity: string; // total to consume (editable)
  overridden: boolean;
}

let lineCounter = 0;

export const MaquilaConsumptionFormPage = () => {
  const navigate = useNavigate();
  const { data: oils = [] } = useOilTypes();
  const { data: rawMaterials = [] } = useRawMaterials();
  const createMutation = useCreateMaquilaConsumption();

  const [clientId, setClientId] = useState("");
  const { data: recipes = [] } = useMaquilaRecipes(clientId || undefined);

  const [recipeId, setRecipeId] = useState("");
  const [quantityProduced, setQuantityProduced] = useState("");
  const [plant, setPlant] = useState("P1");
  const [consumptionDate, setConsumptionDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const [referenceDocument, setReferenceDocument] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState<ConsumeLine[]>([]);

  const selectedRecipe: MaquilaProductRecipe | undefined = recipes.find(
    (r) => r.id === recipeId
  );

  // Reset recipe + lines when the client changes.
  useEffect(() => {
    setRecipeId("");
    setLines([]);
  }, [clientId]);

  // Build lines from the recipe when recipe or quantity changes (respect manual overrides).
  const cantidad = parseFloat(quantityProduced) || 0;
  useEffect(() => {
    if (!selectedRecipe) return;
    setLines(
      (selectedRecipe.ingredients ?? []).map((ing) => {
        const name =
          ing.oil_type?.name || ing.raw_material?.name || "Insumo";
        const code = ing.oil_type?.code || ing.raw_material?.code || null;
        const total = (ing.quantity_per_unit || 0) * cantidad * (ing.waste_factor || 1);
        return {
          key: `line-${lineCounter++}`,
          ingredient_type: ing.ingredient_type,
          oil_type_id: ing.oil_type_id,
          raw_material_id: ing.raw_material_id,
          name,
          code,
          unit: ing.unit,
          quantity_per_unit: ing.quantity_per_unit || 0,
          waste_factor: ing.waste_factor || 1,
          quantity: total ? String(Number(total.toFixed(4))) : "",
          overridden: false,
        };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipeId]);

  // Recompute non-overridden recipe lines when the produced quantity changes.
  useEffect(() => {
    setLines((prev) =>
      prev.map((l) =>
        l.overridden || l.quantity_per_unit <= 0
          ? l
          : {
              ...l,
              quantity: cantidad
                ? String(Number((l.quantity_per_unit * cantidad * l.waste_factor).toFixed(4)))
                : "",
            }
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantityProduced]);

  const ingredientGroups = useMemo(
    () => [
      {
        label: "Aceites",
        options: oils.map((o) => ({
          value: `oil:${o.id}`,
          label: `${o.code} — ${o.name}`,
          searchTerms: `${o.code} ${o.name}`,
        })),
      },
      {
        label: "Materia Prima",
        options: rawMaterials.map((r) => ({
          value: `rm:${r.id}`,
          label: `${r.code} — ${r.name}`,
          searchTerms: `${r.code} ${r.name}`,
        })),
      },
    ],
    [oils, rawMaterials]
  );

  const unitCostFor = (line: ConsumeLine): number => {
    if (line.ingredient_type === "oil" && line.oil_type_id) {
      const oil = oils.find((o) => o.id === line.oil_type_id);
      if (!oil) return 0;
      return line.unit === "kg"
        ? costPerKgClp(oil.average_cost_clp ?? 0, oil.density_kg_per_liter || 0.92)
        : oil.average_cost_clp ?? 0;
    }
    if (line.ingredient_type === "raw_material" && line.raw_material_id) {
      return rawMaterials.find((r) => r.id === line.raw_material_id)?.average_cost_clp ?? 0;
    }
    return 0;
  };

  const lineTotal = (line: ConsumeLine) =>
    (parseFloat(line.quantity) || 0) * unitCostFor(line);

  const grandTotal = lines.reduce((sum, l) => sum + lineTotal(l), 0);

  const updateLine = (key: string, patch: Partial<ConsumeLine>) =>
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const removeLine = (key: string) =>
    setLines((prev) => prev.filter((l) => l.key !== key));

  const addManualLine = (value: string) => {
    const [kind, id] = value.split(":");
    if (kind === "oil") {
      const oil = oils.find((o) => o.id === id);
      if (!oil) return;
      setLines((prev) => [
        ...prev,
        {
          key: `line-${lineCounter++}`,
          ingredient_type: "oil",
          oil_type_id: oil.id,
          raw_material_id: null,
          name: oil.name,
          code: oil.code,
          unit: "kg",
          quantity_per_unit: 0,
          waste_factor: 1,
          quantity: "",
          overridden: true,
        },
      ]);
    } else {
      const rm = rawMaterials.find((r) => r.id === id);
      if (!rm) return;
      setLines((prev) => [
        ...prev,
        {
          key: `line-${lineCounter++}`,
          ingredient_type: "raw_material",
          oil_type_id: null,
          raw_material_id: rm.id,
          name: rm.name,
          code: rm.code,
          unit: rm.unit,
          quantity_per_unit: 0,
          waste_factor: 1,
          quantity: "",
          overridden: true,
        },
      ]);
    }
  };

  const validLines = lines.filter(
    (l) =>
      (l.oil_type_id || l.raw_material_id) && (parseFloat(l.quantity) || 0) > 0
  );

  const isValid =
    !!clientId &&
    !!selectedRecipe &&
    cantidad > 0 &&
    validLines.length > 0;

  const handleSubmit = async () => {
    if (!selectedRecipe) return;
    try {
      await createMutation.mutateAsync({
        client_id: clientId,
        recipe_id: selectedRecipe.id,
        product_name: selectedRecipe.product_name,
        quantity_produced: cantidad,
        plant,
        consumption_date: consumptionDate,
        reference_document: referenceDocument.trim() || undefined,
        notes: notes.trim() || undefined,
        lines: validLines.map((l) => ({
          ingredient_type: l.ingredient_type,
          oil_type_id: l.oil_type_id,
          raw_material_id: l.raw_material_id,
          quantity: parseFloat(l.quantity) || 0,
          unit: l.unit || "UNI",
        })),
      });
      navigate("/maquila/consumptions");
    } catch {
      // handled by mutation toast
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Registrar Consumo de Maquila"
        description="Rebaja los insumos de Aliace entregados al cliente según la cantidad producida"
        back={
          <Button
            variant="ghost"
            className="h-8 px-2 text-muted-foreground"
            onClick={() => navigate("/maquila")}
          >
            ← Volver
          </Button>
        }
      />

      <Card>
        <CardContent className="space-y-4 p-4 sm:p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <MaquilaClientSelect value={clientId} onValueChange={setClientId} />
            <div className="space-y-2">
              <Label>Producto / Receta *</Label>
              <SearchableSelect
                value={recipeId}
                onValueChange={setRecipeId}
                options={recipes.map((r) => ({
                  value: r.id,
                  label: r.product_code
                    ? `${r.product_code} — ${r.product_name}`
                    : r.product_name,
                  searchTerms: `${r.product_code ?? ""} ${r.product_name}`,
                }))}
                placeholder={clientId ? "Seleccionar producto" : "Elija un cliente primero"}
                disabled={!clientId}
                emptyMessage="Este cliente no tiene recetas"
              />
            </div>
            <div className="space-y-2">
              <Label>Cantidad producida ({selectedRecipe?.output_unit || "UNI"}) *</Label>
              <Input
                className="h-9"
                type="number"
                step="0.01"
                min="0"
                value={quantityProduced}
                onChange={(e) => setQuantityProduced(e.target.value)}
                placeholder="Cantidad a fabricar"
              />
            </div>
            <div className="space-y-2">
              <Label>Planta *</Label>
              <Select value={plant} onValueChange={setPlant}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="P1">P1</SelectItem>
                  <SelectItem value="P2">P2</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Fecha</Label>
              <Input
                className="h-9"
                type="date"
                value={consumptionDate}
                onChange={(e) => setConsumptionDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Documento de referencia</Label>
              <Input
                className="h-9"
                value={referenceDocument}
                onChange={(e) => setReferenceDocument(e.target.value)}
                placeholder="Ej: OC #1234"
              />
            </div>
          </div>

          {/* Lines */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Insumos a consumir</Label>
              <div className="w-full sm:w-80">
                <SearchableSelect
                  value=""
                  onValueChange={addManualLine}
                  groups={ingredientGroups}
                  placeholder="+ Agregar insumo extra…"
                />
              </div>
            </div>

            {lines.length === 0 ? (
              <p className="rounded-lg border border-dashed border-gray-300 bg-muted/30 px-4 py-6 text-center text-sm text-muted-foreground">
                {selectedRecipe
                  ? "Ingresa la cantidad producida para calcular el consumo."
                  : "Selecciona un producto/receta para cargar los insumos."}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Insumo</TableHead>
                      <TableHead className="w-28 text-right">Cant. total</TableHead>
                      <TableHead className="w-20">Unidad</TableHead>
                      <TableHead className="w-32 text-right">Costo unit.</TableHead>
                      <TableHead className="w-32 text-right">Costo total</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lines.map((line) => (
                      <TableRow key={line.key} className="hover:bg-muted/30">
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">{line.name}</span>
                            {line.code && (
                              <span className="font-mono text-xs text-muted-foreground">
                                {line.code}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            className="h-8 text-right"
                            type="number"
                            step="0.0001"
                            min="0"
                            value={line.quantity}
                            onChange={(e) =>
                              updateLine(line.key, {
                                quantity: e.target.value,
                                overridden: true,
                              })
                            }
                          />
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {line.unit}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          ${formatCurrency(unitCostFor(line))}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          ${formatCurrency(lineTotal(line))}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => removeLine(line.key)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            <div className="flex items-center justify-between rounded-lg bg-muted/40 px-4 py-3">
              <span className="text-sm text-muted-foreground">
                Costo total del consumo
              </span>
              <span className="text-lg font-semibold">
                ${formatCurrency(grandTotal)}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notas</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Observaciones del consumo…"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => navigate("/maquila")}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={!isValid || createMutation.isPending}>
              {createMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Registrar consumo
            </Button>
          </div>
        </CardContent>
      </Card>
    </PageContainer>
  );
};
